import React, { useEffect, useState } from "react";
import { api, formatIDR, formatDateTime } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DateRangeFilter, { useDateFilter } from "@/components/DateRangeFilter";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trash2, Printer } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { printReceipt } from "@/lib/pdf";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";

export default function Sales() {
  const { isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const byDate = useDateFilter(items, range);
  const tf = useTableFilter(byDate, {
    searchKeys: ["customer_name", "cashier_name", "payment_method"],
    defaultSort: { key: "date", dir: "desc" },
  });
  const totalSum = tf.rows.reduce((s, x) => s + Number(x.total || 0), 0);

  const load = () => api.get("/sales").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Batalkan penjualan? Stok & kas akan dikembalikan.")) return;
    try { await api.delete(`/sales/${id}`); toast.success("Dibatalkan"); load(); }
    catch { toast.error("Gagal"); }
  };

  return (
    <>
      <PageHeader title="Riwayat Penjualan" subtitle="Semua penjualan langsung ke konsumen" testId="page-sales" />

      <div className="space-y-4 mb-4">
        <DateRangeFilter value={range} onChange={setRange}/>
        <Card className="p-4 border border-[var(--hotto-border)] rounded-md flex justify-between items-center">
          <div className="overline">Total di Rentang Tanggal</div>
          <div className="font-mono-num text-xl font-bold text-[var(--hotto-primary)]">{formatIDR(totalSum)} <span className="text-xs font-normal text-slate-500">({tf.rows.length} transaksi)</span></div>
        </Card>
      </div>

      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
        <div className="p-3 border-b border-[var(--hotto-border)]">
          <TableToolbar tf={tf} placeholder="Cari konsumen / kasir..." testId="sales-search"/>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-10">#</TableHead>
              <SortHeader tf={tf} col="date">Tanggal</SortHeader>
              <SortHeader tf={tf} col="customer_name">Konsumen</SortHeader>
              <SortHeader tf={tf} col="cashier_name">Kasir</SortHeader>
              <SortHeader tf={tf} col="payment_method">Metode</SortHeader>
              <TableHead>Item</TableHead>
              <SortHeader tf={tf} col="total" className="text-right">Total</SortHeader>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tf.rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Belum ada penjualan.</TableCell></TableRow>}
            {tf.rows.map((s, idx) => (
              <TableRow key={s.id} className="table-row-hover cursor-pointer" onClick={()=>setDetail(s)}>
                <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                <TableCell className="font-mono-num text-sm">{formatDateTime(s.date)}</TableCell>
                <TableCell className="font-medium">{s.customer_name}</TableCell>
                <TableCell className="text-slate-500">{s.cashier_name}</TableCell>
                <TableCell><Badge variant="outline" className="uppercase text-xs">{s.payment_method}</Badge></TableCell>
                <TableCell className="text-slate-500">{s.items.length} item</TableCell>
                <TableCell className="text-right font-mono-num font-semibold text-[var(--hotto-primary)]">{formatIDR(s.total)}</TableCell>
                <TableCell className="text-right" onClick={(e)=>e.stopPropagation()}>
                  <Button size="icon" variant="ghost" onClick={()=>printReceipt(s)} data-testid={`btn-print-receipt-${s.id}`} title="Cetak Struk">
                    <Printer className="w-4 h-4 text-[var(--hotto-primary)]"/>
                  </Button>
                  {isOwner && <Button size="icon" variant="ghost" onClick={()=>del(s.id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v)=>!v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Detail Penjualan</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-500">Konsumen:</span> <b>{detail.customer_name}</b></div>
                <div><span className="text-slate-500">Tanggal:</span> {formatDateTime(detail.date)}</div>
                <div><span className="text-slate-500">Kasir:</span> {detail.cashier_name}</div>
                <div><span className="text-slate-500">Metode:</span> {detail.payment_method}</div>
              </div>
              <div className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50"><TableHead>Produk</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Harga</TableHead><TableHead className="text-right">Subtotal</TableHead></TableRow></TableHeader>
                  <TableBody>{detail.items.map((it,i)=>(<TableRow key={i}><TableCell>{it.product_name}</TableCell><TableCell className="text-right font-mono-num">{it.qty}</TableCell><TableCell className="text-right font-mono-num">{formatIDR(it.unit_price)}</TableCell><TableCell className="text-right font-mono-num">{formatIDR(it.subtotal)}</TableCell></TableRow>))}</TableBody>
                </Table>
              </div>
              <div className="flex justify-end"><span className="overline mr-3">Total</span><span className="font-mono-num font-bold text-lg">{formatIDR(detail.total)}</span></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
